import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { Users } from 'lucide-react';
import { hideAddAuthorizedGroupModal } from '@@/features/modal';
import { useCreateRoomMutation } from '@@/services/room';
import { RootState } from '@@/store';
import { FieldError, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';
import SelectPropertyResidentsInput from '@@/components/Inputs/SelectPropertyResidentsInput';
import CheckboxInput from '@@/components/Inputs/CheckboxInput';

type RoomFormType = {
  title: string;
  description: string;
  invitedUserIds: { id: string; label: string }[];
  notifyUsers: boolean;
};

const schema = yup
  .object({
    title: yup.string().trim(),
    description: yup.string().trim(),
    invitedUserIds: yup.array().of(
      yup.object({
        id: yup.string().required(),
        label: yup.string(),
      }),
    ),
    notifyUsers: yup.boolean(),
  })
  .required();

const AddAuthorizedGroupModal: React.FC<{ propertyId: string }> = ({
  propertyId,
}) => {
  const dispatch = useDispatch();
  const showAddAuthorizedGroupModal = useSelector(
    (state: RootState) => state.modal.showAddAuthorizedGroupModal,
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RoomFormType>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const closeModal = useCallback(() => {
    dispatch(hideAddAuthorizedGroupModal());
    reset();
  }, [dispatch, reset]);

  const [createRoom, { isSuccess }] = useCreateRoomMutation();

  const onSubmit = useCallback(
    (data: RoomFormType) => {
      if (propertyId) {
        createRoom({
          ...data,
          invitedUserIds: data.invitedUserIds.map((user) => user.id),
          propertyId,
          isPublic: false,
        });
      }
    },
    [createRoom, propertyId],
  );

  useEffect(() => {
    return closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
      toast.success('Le groupe a bien été autorisé', {
        position: 'top-right',
      });
    }
  }, [isSuccess, reset, closeModal]);

  return (
    <Modal
      isOpen={showAddAuthorizedGroupModal}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Show Add Authorized group Modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <Users size={24} className="text-primary-600" />
        <h2 className="text-heading-md text-neutral-900">Ajouter un groupe autorisé</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form className="space-y-6">
        <StandardInput
          register={register}
          id="title"
          label="Titre"
          type="text"
          placeholder="Titre"
          error={errors.title}
        />
        <StandardInput
          register={register}
          id="description"
          label="Description"
          type="textarea"
          placeholder="Description"
          error={errors.description}
        />

        <SelectPropertyResidentsInput
          id="invitedUserIds"
          propertyId={propertyId}
          label="Membres du groupe"
          control={control}
          error={errors.invitedUserIds as FieldError}
        />

        <CheckboxInput
          register={register}
          id="notifyUsers"
          label="Envoyer une notification aux membres du groupe"
        />
      </form>

      <div className="flex justify-end mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button bg-primary-600 hover:bg-primary-700 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
        >
          Valider
        </button>
      </div>
    </Modal>
  );
};

export default AddAuthorizedGroupModal;
